import { useState, useEffect } from "react";
import { Plus, Edit3, Eye, Search, Trash2 } from "lucide-react";
import toast from "react-hot-toast";
import { useAuth } from "../context/AuthContext";
import { DataTable } from "./DataTable/DataTable";
import BlogPagePopUp from "../shared/BlogPagePopUp";
import { BlogDetailsView } from "./BlogDetailsView";
import { BlogService } from "../services/api/blog.service";
import AddNewBlog from "../shared/AddNewBlog";

// ----------- Types -----------
export interface Blog {
  id: string;
  title: string;
  description: string;
  categories: string;
  specialization: string;
  doctorName: string;
  postedAt: string;
  blogPoster: string;
  isDecommissioned?: boolean;
  status?: string;
}

export interface BlogApiResponse {
  success: boolean;
  data: Blog[];
  pagination: {
    totalData: number;
    totalPages: number;
    currentPage: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

// ----------- Component -----------
export function BlogsView() {
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [blogsList, setBlogsList] = useState<Blog[]>([]);
  const [showAddBlog, setShowAddBlog] = useState(false);
  const [showBlogDetails, setShowBlogDetails] = useState(false);
  const [selectedBlog, setSelectedBlog] = useState<string | null>(null);
  const [searchText, setSearchText] = useState("");

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItemsCount, setTotalItemsCount] = useState(0);

  // ----------- API Call -----------
  const loadBlogs = async (page = 1, pageSize = 10, search = "") => {
    try {
      setIsLoading(true);
      const response: BlogApiResponse = await BlogService.getAllBlog(
        page,
        pageSize,
        search,
      );

      if (response.success) {
        setBlogsList(response.data || []);
        setTotalPages(response.pagination.totalPages);
        setTotalItemsCount(response.pagination.totalData);
        setCurrentPage(response.pagination.currentPage);
      } else {
        toast.error("Failed to load blogs");
      }
    } catch (error) {
      console.error("Error fetching blogs:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // ----------- Effects -----------
  useEffect(() => {
    loadBlogs(currentPage, pageSize, searchText);
  }, [currentPage, pageSize, searchText]);

  // ----------- Handlers -----------
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  };

  const handleEditClick = (blog: Blog) => {
    setSelectedBlog(blog.id);
    setShowAddBlog(true);
  };

  const handleDeleteBlog = async (blogId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    try {
      const deleteBlogResponse = await BlogService.deleteBlogs(blogId);

      if (deleteBlogResponse.success) {
        toast.success(deleteBlogResponse.message);
        loadBlogs();
      } else {
        toast.error(deleteBlogResponse.message);
      }
      console.log("\n\n deleteBlogResponse:", deleteBlogResponse, "\n\n");
    } catch (error) {
      console.error(error);
    }
  };

  const handleAddBlogSuccess = () => {
    setShowAddBlog(false);
    setSelectedBlog(null);
    loadBlogs();
  };

  // ----------- Table Columns -----------
  const columns = [
    {
      key: "title",
      title: "Title",
      sortable: true,
      width: "w-[300px]",
      render: (blog: Blog) => (
        <div className="flex items-center">
          <img
            src={blog.blogPoster || "/placeholder.png"}
            alt={blog.title}
            className="w-12 h-10 rounded-md mr-3 object-cover border border-gray-200"
          />
          <div>
            <div className="font-medium text-gray-900 line-clamp-2">
              {blog.title}
            </div>
            <div className="text-sm text-gray-500 capitalize">
              {blog.status || "Active"}
            </div>
          </div>
        </div>
      ),
    },
    {
      key: "categories",
      title: "Category",
      sortable: true,
      width: "w-[150px]",
      render: (blog: Blog) => (
        <div className="font-medium capitalize text-blue-600">
          {blog.categories}
        </div>
      ),
    },
    {
      key: "doctorName",
      title: "Doctor",
      sortable: true,
      width: "w-[200px]",
      render: (blog: Blog) => (
        <div>
          <div className="font-medium">{blog.doctorName}</div>
          <div className="text-sm text-gray-500">{blog.specialization}</div>
        </div>
      ),
    },
    {
      key: "postedAt",
      title: "Created Date",
      sortable: true,
      width: "w-[150px]",
      render: (blog: Blog) => (
        <div className="text-sm">
          {new Date(blog.postedAt).toLocaleDateString()}
        </div>
      ),
    },
    {
      key: "actions",
      title: "Actions",
      width: "w-[200px]",
      render: (blog: Blog) => (
        <div className="flex items-center gap-4">
          {/* View Details */}
          <button
            className="text-blue-600 hover:text-blue-700"
            onClick={(event) => {
              event.stopPropagation();
              setSelectedBlog(blog.id);
              setShowBlogDetails(true);
            }}
            aria-label={`View details of ${blog.title}`}
            title={`View details of ${blog.title}`}
          >
            <Eye className="w-5 h-5" />
          </button>

          {/* Edit (Only Admin) */}
          {user?.role?.toLocaleLowerCase() === "admin" && (
            <button
              className="text-green-600 hover:text-green-700"
              onClick={(event) => {
                event.stopPropagation();
                handleEditClick(blog);
              }}
              title={`Edit ${blog.title}`}
            >
              <Edit3 className="w-5 h-5" />
            </button>
          )}

          {/* Enable/Disable (Only Admin) */}
          {user?.role?.toLocaleLowerCase() === "admin" && (
            <button
              className="rounded-full text-red-600 hover:text-red-700  p-1 px-4"
              onClick={(event) => handleDeleteBlog(blog.id, event)}
              title={`Delete ${blog.title}`}
            >
              <Trash2 className="w-5 h-5" />
            </button>
          )}
        </div>
      ),
    },
  ];

  const addBlogButton = (
    <button
      onClick={() => setShowAddBlog(true)}
      className="relative z-10 flex items-center justify-center px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors whitespace-nowrap"
    >
      <Plus className="w-5 h-5 mr-2" />
      Add Blog
    </button>
  );

  if (showAddBlog) {
    return (
      <AddNewBlog
        blogId={selectedBlog}
        onClose={() => {
          setShowAddBlog(false);
          setSelectedBlog(null);
        }}
        onSuccess={() => {
          setShowAddBlog(false);
          setSelectedBlog(null);
          loadBlogs();
        }}
      />
    );
  }

  // ----------- Render -----------
  return (
    <div className="relative pb-2">
      {!showBlogDetails && (
        <div className="flex justify-end relative top-9">
          <div>
            <Search className="relative top-[2.25rem] left-4 -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search blogs..."
              value={searchText}
              onChange={handleSearchChange}
              className="border rounded-lg px-8 py-2.5  text-sm w-64 text-center"
            />
          </div>
        </div>
      )}

      {showBlogDetails && selectedBlog ? (
        <BlogDetailsView
          selectedBlog={selectedBlog}
          onClose={() => {
            setShowBlogDetails(false);
            setSelectedBlog(null);
          }}
        />
      ) : (
        <DataTable
          data={blogsList}
          columns={columns}
          itemsPerPage={pageSize}
          isLoading={isLoading}
          onRowClick={(blog) => {
            setSelectedBlog(blog.id);
            setShowBlogDetails(true);
          }}
          customFilters={addBlogButton}
          pagination={{
            currentPage,
            setCurrentPage,
            totalPages,
            pageSize,
            totalItemsCount,
          }}
        />
      )}
    </div>
  );
}
